import React, { useState, useEffect } from 'react';

interface ImageWithChainFallbackProps {
  srcChain: (string | null | undefined)[];
  alt: string;
  className?: string;
  loading?: "lazy" | "eager";
}

const ImageWithChainFallback: React.FC<ImageWithChainFallbackProps> = ({
  srcChain,
  alt,
  ...props
}) => {
  // 1. Create a clean list of valid, non-empty image sources from the props
  const validSources = srcChain.filter(src => src && src.trim() !== '');

  // 2. State to track which image in the chain we are currently trying to load
  const [currentIndex, setCurrentIndex] = useState(0);

  // 3. Reset the component if the product (and thus the srcChain) changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [JSON.stringify(validSources)]); // Use JSON.stringify to ensure effect runs on content change

  const handleImageError = () => {
    // 4. If an error occurs, try the next image in the chain
    if (currentIndex < validSources.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  // 5. If we have run out of valid images to try, render the placeholder
  if (currentIndex >= validSources.length) {
    return (
      <div 
        className="image-placeholder" 
        style={{ 
            height: '100%', 
            width: '100%', 
            backgroundColor: '#f0f0f0',
            border: '1px solid #e0e0e0'
        }} 
        aria-label={alt} 
      />
    );
  }

  const currentSrc = validSources[currentIndex];
  
  // 3. If we don't have a valid source at the current index, we have exhausted all options.
  if (!currentSrc) {
    return (
      <div 
        className="image-placeholder" 
        style={{ 
            height: '100%', 
            width: '100%', 
            backgroundColor: '#f0f0f0',
            border: '1px solid #e0e0e0'
        }} 
        aria-label={alt} 
      />
    );
  }  

  // 6. Otherwise, render the img tag with the current source from our chain
  return (
    <img
      src={currentSrc} // This is now guaranteed to be a string
      alt={alt}
      onError={handleImageError}
      {...props}
    />
  );
};

export default ImageWithChainFallback;